import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  DialogActions,
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  Chip,
} from "@mui/material";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Helper: get months and years for selectors
const getMonthOptions = () => [
  { value: 0, label: "All Months" },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString("default", { month: "long" }),
  })),
];

const getYearOptions = (records) => {
  const years = Array.from(
    new Set(
      records.map((r) => r.createdAt?.toDate().getFullYear()).filter(Boolean),
    ),
  );
  const current = new Date().getFullYear();
  const allYears = [current, ...years].filter(
    (y, idx, arr) => arr.indexOf(y) === idx,
  );
  return [
    { value: 0, label: "All Years" },
    ...allYears
      .sort((a, b) => b - a)
      .map((year) => ({
        value: year,
        label: year,
      })),
  ];
};

const getProjectColor = (projectId) => {
  if (!projectId) return "default";
  const colors = [
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ];
  let sum = 0;
  for (let i = 0; i < projectId.length; i++) {
    sum += projectId.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

export default function PayrollHistory() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selectedToDelete, setSelectedToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [month, setMonth] = useState(0); // 0 = All
  const [year, setYear] = useState(0); // 0 = All
  const [search, setSearch] = useState("");

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((rec) => {
      const date = rec.createdAt?.toDate ? rec.createdAt.toDate() : null;
      if (!date) return false;
      // Filter by month/year
      if (month && date.getMonth() + 1 !== month) return false;
      if (year && date.getFullYear() !== year) return false;
      // Filter by name
      if (search && !rec.name.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [records, month, year, search]);

  // Payroll sum for filtered view
  const filteredTotal = filteredRecords.reduce(
    (sum, rec) => sum + parseFloat(rec.amount || 0),
    0,
  );

  useEffect(() => {
    const fetchSelectableData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const empQ = query(
        collection(db, "employees"),
        where("uid", "==", user.uid),
      );
      const empSnap = await getDocs(empQ);
      setEmployees(empSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      const projQ = query(
        collection(db, "projects"),
        where("uid", "==", user.uid),
      );
      const projSnap = await getDocs(projQ);
      setProjects(projSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchSelectableData();

    const q = query(
      collection(db, "payroll"),
      where("uid", "==", auth.currentUser.uid),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRecords(
        data.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate()),
      );
    });
    return () => unsubscribe();
  }, []);

  const handleDelete = async () => {
    if (selectedToDelete) {
      await deleteDoc(doc(db, "payroll", selectedToDelete));
      setSelectedToDelete(null);
      setSnackbarMsg("Payroll entry deleted successfully.");
      setSnackbarOpen(true);
    }
  };

  const handleUpdate = async () => {
    const ref = doc(db, "payroll", editing.id);
    const selectedEmp =
      employees.find((e) => e.id === editing.employeeId) || {};
    const finalName = selectedEmp.name || editing.name;

    await updateDoc(ref, {
      amount: parseFloat(editing.amount),
      name: finalName,
      employeeId: editing.employeeId || null,
      projectId: editing.projectId || null,
      notes: editing.notes || "",
      createdAt: Timestamp.fromDate(editing.date),
    });
    setEditing(null);
    setSnackbarMsg("Payroll entry updated.");
    setSnackbarOpen(true);
  };

  // Preset filter buttons (quick actions)
  const handlePreset = (type) => {
    const now = new Date();
    if (type === "thisMonth") {
      setMonth(now.getMonth() + 1);
      setYear(now.getFullYear());
    } else if (type === "lastMonth") {
      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setMonth(last.getMonth() + 1);
      setYear(last.getFullYear());
    } else {
      setMonth(0);
      setYear(0);
    }
  };

  return (
    <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
        Payroll History
      </Typography>

      {/* Payroll summary for current view */}
      <Box sx={{ mb: 2 }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 2,
            background: "linear-gradient(90deg, #d1fae5 0%, #fef9c3 100%)",
            color: "#334155",
            fontWeight: 600,
            maxWidth: 360,
            boxShadow: "0 2px 8px #c1c1c123",
          }}
        >
          <span role="img" aria-label="euro">
            💶
          </span>{" "}
          Total Payroll in View:{" "}
          <span style={{ fontWeight: 700 }}>
            €
            {filteredTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </span>
        </Paper>
      </Box>

      {/* Filter bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <TextField
          select
          label="Month"
          size="small"
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {getMonthOptions().map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Year"
          size="small"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ minWidth: 120 }}
        >
          {getYearOptions(records).map((y) => (
            <MenuItem key={y.value} value={y.value}>
              {y.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          placeholder="Search employee"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant={
              month === new Date().getMonth() + 1 &&
              year === new Date().getFullYear()
                ? "contained"
                : "outlined"
            }
            onClick={() => handlePreset("thisMonth")}
          >
            This Month
          </Button>
          <Button
            variant={
              month ===
                new Date(
                  new Date().setMonth(new Date().getMonth() - 1),
                ).getMonth() +
                  1 &&
              year ===
                new Date(
                  new Date().setMonth(new Date().getMonth() - 1),
                ).getFullYear()
                ? "contained"
                : "outlined"
            }
            onClick={() => handlePreset("lastMonth")}
          >
            Last Month
          </Button>
          <Button
            variant={month === 0 && year === 0 ? "contained" : "outlined"}
            onClick={() => handlePreset("all")}
          >
            All
          </Button>
        </Stack>
      </Stack>

      {/* Table */}
      <TableContainer sx={{ borderRadius: 3, boxShadow: 0, maxHeight: 550 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: "#F9FAFB" }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Amount (€)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 5, color: "text.secondary" }}
                >
                  No payroll entries found for your filter.
                </TableCell>
              </TableRow>
            ) : (
              filteredRecords.map((rec) => (
                <TableRow key={rec.id} hover>
                  <TableCell>{rec.name}</TableCell>
                  <TableCell>
                    €
                    {Number(rec.amount).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>
                    {rec.createdAt?.toDate
                      ? format(rec.createdAt.toDate(), "do MMM yyyy")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {rec.projectId ? (
                      <Chip
                        label={
                          projects.find((p) => p.id === rec.projectId)?.name ||
                          "Unknown"
                        }
                        color={getProjectColor(rec.projectId)}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{rec.notes || "-"}</TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <IconButton
                        onClick={() =>
                          setEditing({
                            ...rec,
                            date: rec.createdAt?.toDate() || new Date(),
                            employeeId: rec.employeeId || "",
                          })
                        }
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "rgba(0, 56, 255, 0.08)",
                          },
                        }}
                        aria-label="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setSelectedToDelete(rec.id)}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.07)",
                          },
                        }}
                        aria-label="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Payroll</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              label="Employee"
              fullWidth
              value={editing?.employeeId || ""}
              onChange={(e) =>
                setEditing({ ...editing, employeeId: e.target.value })
              }
            >
              {employees.length === 0 && (
                <MenuItem value={editing?.employeeId || ""}>
                  {editing?.name || "Unknown"}
                </MenuItem>
              )}
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Amount (€)"
              type="number"
              fullWidth
              value={editing?.amount || ""}
              onChange={(e) =>
                setEditing({ ...editing, amount: e.target.value })
              }
            />

            <TextField
              select
              label="Project (optional)"
              fullWidth
              value={editing?.projectId || ""}
              onChange={(e) =>
                setEditing({ ...editing, projectId: e.target.value })
              }
            >
              <MenuItem value="">None</MenuItem>
              {projects.map((proj) => (
                <MenuItem key={proj.id} value={proj.id}>
                  {proj.name}
                </MenuItem>
              ))}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Payment Date"
                value={editing?.date || null}
                onChange={(newDate) =>
                  setEditing({ ...editing, date: newDate })
                }
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <TextField
              label="Notes (optional)"
              multiline
              minRows={2}
              fullWidth
              value={editing?.notes || ""}
              onChange={(e) =>
                setEditing({ ...editing, notes: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={!!selectedToDelete}
        onClose={() => setSelectedToDelete(null)}
      >
        <DialogTitle>Delete Payroll Entry</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payroll entry? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedToDelete(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
